import React, { useContext, useEffect, useCallback, useRef } from 'react'
import gsap from 'gsap'
import PropTypes from 'prop-types'
import { StoreState } from '../../Store'

// SVGs
import ExpeditionRouteCircle from '../svgs/ExpeditionRoutes/border'
import ExpeditionRouteMarker from '../svgs/ExpeditionRoutes/marker'
import ExpeditionRoute1 from '../svgs/ExpeditionRoutes/1/1'
import ExpeditionRoute2 from '../svgs/ExpeditionRoutes/1/2'
import ExpeditionRoute3 from '../svgs/ExpeditionRoutes/1/3'
import ExpeditionRoute4 from '../svgs/ExpeditionRoutes/1/4'

// Styles
import {
  SingleRouteMain,
  SingleRoutePath,
  SingleRouteLabel,
  SingleRouteLocation,
  SingleRoutePhoto,
} from './index.style'

// Images
import Rio from '../Map/assets/locations/christ-the-redeemer.jpg'
import Buenos from '../Map/assets/locations/buenos.jpeg'
import MachuPichu from '../Map/assets/locations/machu-pichu.jpg'

const Route1 = ({ id, backgroundColor, labelColor }) => {
  const store = useContext(StoreState)
  const { waiting } = store

  const routeIn = useRef()

  const line1Ref = {
      line: useRef(),
      arrow: useRef(),
    },
    line2Ref = {
      line: useRef(),
      arrow: useRef(),
    },
    line3Ref = {
      line: useRef(),
      arrow: useRef(),
    },
    line4Ref = {
      line: useRef(),
    },
    circle1Ref = useRef(),
    circle2Ref = useRef(),
    circle3Ref = useRef(),
    photo1Ref = useRef(),
    photo2Ref = useRef(),
    photo3Ref = useRef(),
    label1Ref = useRef(),
    label2Ref = useRef(),
    label3Ref = useRef()

  const play = useCallback(() => {
    routeIn.current.play(0)
  }, [routeIn])

  useEffect(() => {
    if (!waiting) setTimeout(play, 500)
  }, [waiting, play])

  useEffect(() => {
    if (routeIn.current) return

    routeIn.current = gsap.timeline({
      paused: true,
    })

    routeIn.current.to(line1Ref.line.current, {
      ease: 'power1.inOut',
      strokeDashoffset: 578,
      duration: 1.5,
    })

    routeIn.current.to(
      line1Ref.arrow.current,
      {
        ease: 'power2.out',
        opacity: 1,
        duration: 0.4,
      },
      '=-.3'
    )

    routeIn.current.addLabel('location1', '-=.5')

    routeIn.current.to(
      circle1Ref.current,
      {
        ease: 'power2.inOut',
        strokeDashoffset: 0,
        duration: 1.5,
      },
      'location1'
    )

    routeIn.current.to(
      photo1Ref.current,
      {
        ease: 'power1.inOut',
        opacity: 1,
        duration: 1,
      },
      'location1+=.5'
    )

    routeIn.current.to(
      label1Ref.current,
      {
        ease: 'power2.out',
        opacity: 1,
        y: 0,
        duration: 0.8,
      },
      'location1+=.7'
    )

    // Line 2

    routeIn.current.to(
      line2Ref.line.current,
      {
        ease: 'power1.inOut',
        strokeDashoffset: 1052,
        duration: 3.5,
      },
      '=-1'
    )

    routeIn.current.to(
      line2Ref.arrow.current,
      {
        ease: 'power2.out',
        opacity: 1,
        duration: 0.4,
      },
      '=-.3'
    )

    routeIn.current.addLabel('location2', '-=.5')

    routeIn.current.to(
      circle2Ref.current,
      {
        ease: 'power2.inOut',
        strokeDashoffset: 0,
        duration: 1.5,
      },
      'location2'
    )

    routeIn.current.to(
      photo2Ref.current,
      {
        ease: 'power1.inOut',
        opacity: 1,
        duration: 1,
      },
      'location2+=.5'
    )

    routeIn.current.to(
      label2Ref.current,
      {
        ease: 'power2.out',
        opacity: 1,
        y: 0,
        duration: 0.8,
      },
      'location2+=.7'
    )

    // Line 3

    routeIn.current.to(line3Ref.line.current, {
      ease: 'power1.inOut',
      strokeDashoffset: 382,
      duration: 1.25,
    })

    routeIn.current.to(
      line3Ref.arrow.current,
      {
        ease: 'power2.out',
        opacity: 1,
        duration: 0.4,
      },
      '=-.3'
    )

    routeIn.current.addLabel('location3', '-=.5')

    routeIn.current.to(
      circle3Ref.current,
      {
        ease: 'power2.inOut',
        strokeDashoffset: 0,
        duration: 1.5,
      },
      'location3'
    )

    routeIn.current.to(
      photo3Ref.current,
      {
        ease: 'power1.inOut',
        opacity: 1,
        duration: 1,
      },
      'location3+=.5'
    )

    routeIn.current.to(
      label3Ref.current,
      {
        ease: 'power2.out',
        opacity: 1,
        y: 0,
        duration: 0.8,
      },
      'location3+=.7'
    )

    // Line 4

    routeIn.current.to(line4Ref.line.current, {
      ease: 'power1.inOut',
      strokeDashoffset: 324,
      duration: 1.1,
    })
  }, [
    routeIn,
    line1Ref.line,
    line1Ref.arrow,
    line2Ref.line,
    line2Ref.arrow,
    line3Ref.line,
    line3Ref.arrow,
    line4Ref.line,
    line4Ref.arrow,
    circle1Ref,
    circle2Ref,
    circle3Ref,
    photo1Ref,
    photo2Ref,
    photo3Ref,
    label1Ref,
    label2Ref,
    label3Ref,
  ])

  return (
    <SingleRouteMain>
      <SingleRoutePath right={8} top={0} width={44.3}>
        <ExpeditionRoute1
          forwardRef={line1Ref}
          mask={backgroundColor}
          stroke={labelColor}
        />
      </SingleRoutePath>
      <SingleRouteLocation right={20.5} top={47}>
        <SingleRouteLabel ref={label1Ref} left color={labelColor}>
          Rio De Janeiro <ExpeditionRouteMarker fill={labelColor} />
        </SingleRouteLabel>
        <ExpeditionRouteCircle
          forwardRef={circle1Ref}
          stroke={labelColor}
          rotation={-42}
        />
        <SingleRoutePhoto ref={photo1Ref} bgImage={Rio} />
      </SingleRouteLocation>
      <SingleRoutePath right={28} top={53} width={50.6}>
        <ExpeditionRoute2
          forwardRef={line2Ref}
          mask={backgroundColor}
          stroke={labelColor}
        />
      </SingleRoutePath>
      <SingleRouteLocation right={74} top={67.5}>
        <SingleRouteLabel ref={label2Ref} right color={labelColor}>
          <ExpeditionRouteMarker fill={labelColor} /> Buenos Aires
        </SingleRouteLabel>
        <ExpeditionRouteCircle
          forwardRef={circle2Ref}
          stroke={labelColor}
          rotation={90}
        />
        <SingleRoutePhoto ref={photo2Ref} bgImage={Buenos} />
      </SingleRouteLocation>
      <SingleRoutePath right={73.5} top={25.6} width={13.1}>
        <ExpeditionRoute3
          forwardRef={line3Ref}
          mask={backgroundColor}
          stroke={labelColor}
        />
      </SingleRoutePath>
      <SingleRouteLocation right={84} top={18.3}>
        <SingleRouteLabel ref={label3Ref} right color={labelColor}>
          <ExpeditionRouteMarker fill={labelColor} /> Machu Picchu
        </SingleRouteLabel>
        <ExpeditionRouteCircle
          forwardRef={circle3Ref}
          stroke={labelColor}
          rotation={65}
        />
        <SingleRoutePhoto ref={photo3Ref} bgImage={MachuPichu} />
      </SingleRouteLocation>
      <SingleRoutePath right={89.3} top={-1.3} width={26.1}>
        <ExpeditionRoute4
          forwardRef={line4Ref}
          mask={backgroundColor}
          stroke={labelColor}
        />
      </SingleRoutePath>
    </SingleRouteMain>
  )
}

Route1.propTypes = {
  id: PropTypes.string.isRequired,
  backgroundColor: PropTypes.string,
  labelColor: PropTypes.string,
}

export default Route1
